import React from 'react';

/**
 * Lightweight markdown renderer for chat bubbles.
 * Supports: **bold**, *italic*, `inline code`, unordered/ordered lists, paragraphs.
 */
export default function MarkdownText({ text }) {
  if (!text) return null;

  const paragraphs = text.split(/\n{2,}/);

  return (
    <div className="space-y-2">
      {paragraphs.map((para, pi) => renderBlock(para.trim(), pi))}
    </div>
  );
}

function renderBlock(block, key) {
  const lines = block.split('\n');

  // Detect list: all non-empty lines start with `- `, `* `, or `1. `
  const isUnordered = lines.every((l) => !l.trim() || /^[-*]\s/.test(l.trim()));
  const isOrdered = lines.every((l) => !l.trim() || /^\d+\.\s/.test(l.trim()));

  if (isUnordered && lines.some((l) => l.trim())) {
    return (
      <ul key={key} className="list-disc pl-5 space-y-0.5">
        {lines.filter((l) => l.trim()).map((l, i) => (
          <li key={i}>{renderInline(l.trim().replace(/^[-*]\s/, ''))}</li>
        ))}
      </ul>
    );
  }

  if (isOrdered && lines.some((l) => l.trim())) {
    return (
      <ol key={key} className="list-decimal pl-5 space-y-0.5">
        {lines.filter((l) => l.trim()).map((l, i) => (
          <li key={i}>{renderInline(l.trim().replace(/^\d+\.\s/, ''))}</li>
        ))}
      </ol>
    );
  }

  // Plain paragraph — preserve single newlines
  return (
    <p key={key} className="whitespace-pre-wrap">
      {renderInline(block)}
    </p>
  );
}

function renderInline(text) {
  // Split on bold, italic, and inline code patterns
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let last = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));

    if (match[2]) {
      parts.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={match.index}>{match[3]}</em>);
    } else if (match[4]) {
      parts.push(
        <code key={match.index} className="bg-base-200 px-1 py-0.5 rounded text-sm font-mono">
          {match[4]}
        </code>
      );
    }
    last = match.index + match[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}
